import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Spinner,
  Banner,
  Badge,
  Box,
  Divider,
  DataTable,
  Modal,
  FormLayout,
  TextField,
  Select,
} from '@shopify/polaris';

import { useAdminAuth } from '../../providers/AdminAuthProvider';

export default function AdminStoreDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { isAuthenticated, adminFetch } = useAdminAuth();

  const [store, setStore] = useState(null);
  const [bars, setBars] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modals
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [trialDays, setTrialDays] = useState('30');
  const [discountType, setDiscountType] = useState('percentage');
  const [discountValue, setDiscountValue] = useState('');
  const [discountDescription, setDiscountDescription] = useState('');
  const [discountExpiresDays, setDiscountExpiresDays] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const loadStore = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await adminFetch(`/api/admin/stores/${id}`);
      if (response.ok) {
        const data = await response.json();
        setStore(data.store);
        setBars(data.store?.bars || []);
        setDiscounts(data.store?.discounts || []);
      } else {
        setError('Failed to load store details');
      }
    } catch (err) {
      setError('Failed to load store details');
    } finally {
      setIsLoading(false);
    }
  }, [adminFetch, id]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    loadStore();
  }, [isAuthenticated, navigate, loadStore]);

  const handleUpgrade = async () => {
    setActionLoading(true);
    try {
      const response = await adminFetch(`/api/admin/stores/${id}/upgrade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trial_days: parseInt(trialDays) || 0 }),
      });
      if (response.ok) {
        setShowUpgradeModal(false);
        loadStore();
      } else {
        setError('Failed to upgrade store');
      }
    } catch (err) {
      setError('Failed to upgrade store');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDowngrade = async () => {
    if (!window.confirm('Downgrade this store to basic plan?')) return;
    try {
      const response = await adminFetch(`/api/admin/stores/${id}/downgrade`, {
        method: 'POST',
      });
      if (response.ok) {
        loadStore();
      } else {
        setError('Failed to downgrade store');
      }
    } catch (err) {
      setError('Failed to downgrade store');
    }
  };

  const handleCreateDiscount = async () => {
    if (!discountValue) return;
    setActionLoading(true);
    try {
      const response = await adminFetch('/api/admin/discounts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          store_id: parseInt(id),
          discount_type: discountType,
          discount_value: parseFloat(discountValue),
          description: discountDescription,
          expires_in_days: discountExpiresDays ? parseInt(discountExpiresDays) : null,
        }),
      });
      if (response.ok) {
        setShowDiscountModal(false);
        setDiscountValue('');
        setDiscountDescription('');
        setDiscountExpiresDays('');
        loadStore();
      } else {
        setError('Failed to create discount');
      }
    } catch (err) {
      setError('Failed to create discount');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeDiscount = async (discountId) => {
    if (!window.confirm('Revoke this discount?')) return;
    try {
      const response = await adminFetch(`/api/admin/discounts/${discountId}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        loadStore();
      } else {
        setError('Failed to revoke discount');
      }
    } catch (err) {
      setError('Failed to revoke discount');
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  if (isLoading) {
    return (
      <Page
        title="Store Details"
        backAction={{ content: 'Stores', onAction: () => navigate('/admin/stores') }}
      >
        <Layout>
          <Layout.Section>
            <Card>
              <Box padding="400">
                <InlineStack align="center">
                  <Spinner size="large" />
                </InlineStack>
              </Box>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (!store) {
    return (
      <Page
        title="Store Details"
        backAction={{ content: 'Stores', onAction: () => navigate('/admin/stores') }}
      >
        <Layout>
          <Layout.Section>
            <Banner tone="critical">Store not found</Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const barRows = bars.map(bar => [
    bar.name || 'Unnamed Bar',
    `$${bar.free_shipping_threshold}`,
    bar.position,
    <Badge tone={bar.is_active ? 'success' : 'info'}>
      {bar.is_active ? 'Active' : 'Inactive'}
    </Badge>,
    new Date(bar.created_at).toLocaleDateString(),
  ]);

  const formatDiscountValue = (discount) => {
    if (discount.discount_type === 'percentage') return `${discount.discount_value}%`;
    if (discount.discount_type === 'free_trial') return `${discount.discount_value} days`;
    return `$${discount.discount_value}`;
  };

  const discountRows = discounts.map(discount => [
    formatDiscountValue(discount),
    discount.description || '-',
    <Badge tone={discount.is_active ? 'success' : 'info'}>
      {discount.is_active ? 'Active' : 'Inactive'}
    </Badge>,
    discount.expires_at
      ? new Date(discount.expires_at).toLocaleDateString()
      : 'Never',
    <Button
      size="slim"
      tone="critical"
      onClick={() => handleRevokeDiscount(discount.id)}
      disabled={!discount.is_active}
    >
      Revoke
    </Button>,
  ]);

  return (
    <Page
      title={store.shop_domain}
      subtitle={store.shop_name}
      backAction={{ content: 'Stores', onAction: () => navigate('/admin/stores') }}
      secondaryActions={[
        store.plan_type !== 'premium' && {
          content: 'Upgrade to Premium',
          onAction: () => setShowUpgradeModal(true),
        },
        store.plan_type === 'premium' && {
          content: 'Downgrade to Basic',
          onAction: handleDowngrade,
          destructive: true,
        },
        {
          content: 'Add Discount',
          onAction: () => setShowDiscountModal(true),
        },
      ].filter(Boolean)}
    >
      {error && (
        <Box paddingBlockEnd="400">
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        </Box>
      )}

      <Layout>
        {/* Store Info */}
        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd">Store Information</Text>
              <Divider />

              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text tone="subdued">Domain</Text>
                  <Text>{store.shop_domain}</Text>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text tone="subdued">Name</Text>
                  <Text>{store.shop_name || '-'}</Text>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text tone="subdued">Email</Text>
                  <Text>{store.shop_email || '-'}</Text>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text tone="subdued">Currency</Text>
                  <Text>{store.shop_currency || '-'}</Text>
                </InlineStack>
              </BlockStack>

              <Divider />

              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text tone="subdued">Plan</Text>
                  <Badge tone={store.plan_type === 'premium' ? 'success' : 'info'}>
                    {store.plan_type}
                  </Badge>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text tone="subdued">Status</Text>
                  <Badge tone={store.is_installed ? 'success' : 'critical'}>
                    {store.is_installed ? 'Installed' : 'Uninstalled'}
                  </Badge>
                </InlineStack>
                {store.plan_started_at && (
                  <InlineStack align="space-between">
                    <Text tone="subdued">Plan Started</Text>
                    <Text>{new Date(store.plan_started_at).toLocaleDateString()}</Text>
                  </InlineStack>
                )}
                {store.trial_ends_at && (
                  <InlineStack align="space-between">
                    <Text tone="subdued">Trial Ends</Text>
                    <Text>{new Date(store.trial_ends_at).toLocaleDateString()}</Text>
                  </InlineStack>
                )}
              </BlockStack>

              <Divider />

              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text tone="subdued">Installed At</Text>
                  <Text>{store.installed_at ? new Date(store.installed_at).toLocaleDateString() : '-'}</Text>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text tone="subdued">Total Bars</Text>
                  <Text>{bars.length}</Text>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text tone="subdued">Active Bars</Text>
                  <Text>{bars.filter(b => b.is_active).length}</Text>
                </InlineStack>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Bars and Discounts */}
        <Layout.Section>
          <BlockStack gap="400">
            {/* Shipping Bars */}
            <Card>
              <BlockStack gap="400">
                <Text variant="headingMd">Shipping Bars ({bars.length})</Text>
                {bars.length > 0 ? (
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text', 'text']}
                    headings={['Name', 'Threshold', 'Position', 'Status', 'Created']}
                    rows={barRows}
                  />
                ) : (
                  <Text tone="subdued">No shipping bars created yet.</Text>
                )}
              </BlockStack>
            </Card>

            {/* Discounts */}
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between">
                  <Text variant="headingMd">Discounts ({discounts.length})</Text>
                  <Button size="slim" onClick={() => setShowDiscountModal(true)}>
                    Add Discount
                  </Button>
                </InlineStack>
                {discounts.length > 0 ? (
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text', 'text']}
                    headings={['Value', 'Description', 'Status', 'Expires', 'Actions']}
                    rows={discountRows}
                  />
                ) : (
                  <Text tone="subdued">No discounts applied to this store.</Text>
                )}
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>

      {/* Upgrade Modal */}
      <Modal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title="Upgrade to Premium"
        primaryAction={{
          content: 'Upgrade',
          onAction: handleUpgrade,
          loading: actionLoading,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setShowUpgradeModal(false) },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Trial Days"
              type="number"
              value={trialDays}
              onChange={setTrialDays}
              helpText="Number of free trial days (0 for no trial)"
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Discount Modal */}
      <Modal
        open={showDiscountModal}
        onClose={() => setShowDiscountModal(false)}
        title="Create Discount"
        primaryAction={{
          content: 'Create',
          onAction: handleCreateDiscount,
          loading: actionLoading,
          disabled: !discountValue,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setShowDiscountModal(false) },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <Select
              label="Discount Type"
              options={[
                { label: 'Percentage', value: 'percentage' },
                { label: 'Fixed Amount', value: 'fixed' },
                { label: 'Free Trial Extension', value: 'free_trial' },
              ]}
              value={discountType}
              onChange={setDiscountType}
            />
            <TextField
              label={
                discountType === 'percentage'
                  ? 'Discount (%)'
                  : discountType === 'free_trial'
                    ? 'Extra Trial Days'
                    : 'Discount ($)'
              }
              type="number"
              value={discountValue}
              onChange={setDiscountValue}
              helpText={
                discountType === 'free_trial'
                  ? 'Number of days added on top of the standard trial'
                  : undefined
              }
            />
            <TextField
              label="Description (optional)"
              value={discountDescription}
              onChange={setDiscountDescription}
            />
            <TextField
              label="Expires in (days)"
              type="number"
              value={discountExpiresDays}
              onChange={setDiscountExpiresDays}
              helpText="Leave empty for no expiration"
            />
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
